// src/components/ui/sections/TeamMemberEditor/TeamMemberEditorShadcn.jsx
import { useState, useEffect } from 'react';
import {
  User,
  Shield,
  Activity,
  Crown,
  Mail,
  AlertCircle,
  Trash2
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../tabs';
import { Badge } from '../../badge';
import { Avatar, AvatarFallback } from '../../avatar';
import { Button } from '../../button';

// Import custom components (keeping existing ones for permissions and overview)
import TeamMemberDetailsFormShadcn from '../../forms/TeamMemberDetailsForm/TeamMemberDetailsFormShadcn';
import TeamMemberPermissions from '../../sections/TeamMemberPermissions';
import TeamMemberOverview from '../../sections/TeamMemberOverview';

import { cn } from '../../../../utils/cn';

const TeamMemberEditorShadcn = ({
  member,
  currentUser,
  onUpdateMember,
  onUpdateRole,
  onRemoveMember,
  onReactivateMember,
  permissions = {},
  savingMember = false,
  savingRole = false
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Reset tab when member changes
  useEffect(() => {
    setActiveTab('details');
    setHasUnsavedChanges(false);
  }, [member?.id]);

  // Empty state when no member selected
  if (!member) {
    return (
      <Card className="h-full flex items-center justify-center">
        <CardContent className="text-center">
          <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <CardTitle className="mb-2">Select a Team Member</CardTitle>
          <CardDescription className="max-w-sm">
            Choose a team member from the list to view and manage their details and permissions.
          </CardDescription>
        </CardContent>
      </Card>
    );
  }

  // Check if current user can edit this member
  const canEditMember = permissions.canManageTeam && member.id !== currentUser?.id;
  const canChangeRole = permissions.canUpdateTeamRoles && member.id !== currentUser?.id;

  // Role configuration for display
  const getRoleConfig = (role) => {
    switch (role) {
      case 'admin':
        return {
          icon: Crown,
          label: 'Administrator',
          variant: 'default',
          className: 'bg-amber-500/20 text-amber-300 border-amber-500/30'
        };
      case 'manager':
        return {
          icon: Shield,
          label: 'Manager',
          variant: 'secondary',
          className: 'bg-purple-500/20 text-purple-300 border-purple-500/30'
        };
      case 'staff':
        return {
          icon: User,
          label: 'Staff',
          variant: 'outline',
          className: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
        };
      default:
        return {
          icon: User,
          label: 'Unknown',
          variant: 'outline',
          className: 'bg-slate-500/20 text-slate-300 border-slate-500/30'
        };
    }
  };

  const roleConfig = getRoleConfig(member.role);
  const RoleIcon = roleConfig.icon;

  // Generate initials for avatar
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Card className="h-full flex flex-col">
      {/* Header */}
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {/* Member Avatar */}
            <Avatar className="h-16 w-16">
              <AvatarFallback className="text-lg font-semibold">
                {getInitials(member.name)}
              </AvatarFallback>
            </Avatar>

            <div>
              <CardTitle className="flex items-center gap-2">
                {member.name}
                {member.id === currentUser?.id && (
                  <Badge variant="secondary" className="text-xs">
                    You
                  </Badge>
                )}
                {!member.is_active && (
                  <Badge variant="secondary" className="text-xs bg-red-500/20 text-red-300 border-red-500/30">
                    Inactive
                  </Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <CardDescription>{member.email}</CardDescription>
              </div>

              {/* Role Badge */}
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={roleConfig.variant} className={cn("text-sm", roleConfig.className)}>
                  <RoleIcon className="w-4 h-4 mr-1" />
                  {roleConfig.label}
                </Badge>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <div className="flex items-center gap-2 text-amber-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>Unsaved changes</span>
              </div>
            )}

            {/* Deactivate/Reactivate Buttons - only show if allowed */}
            {permissions.canRemoveTeamMembers && canEditMember && (
              <>
                {member.is_active ? (
                  onRemoveMember && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Deactivate ${member.name}? They will be logged out and won't be able to access the system until reactivated.`)) {
                          onRemoveMember(member.id);
                        }
                      }}
                      disabled={savingMember || savingRole}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Deactivate
                    </Button>
                  )
                ) : (
                  onReactivateMember && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Reactivate ${member.name}? They will be able to log in again.`)) {
                          onReactivateMember(member.id);
                        }
                      }}
                      disabled={savingMember || savingRole}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Activity className="w-4 h-4 mr-2" />
                      Reactivate
                    </Button>
                  )
                )}
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Member Details
            </TabsTrigger>
            <TabsTrigger value="permissions" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Permissions
            </TabsTrigger>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Overview
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>

      {/* Tab Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <Tabs value={activeTab} className="h-full">
          <TabsContent value="details" className="h-full m-0">
            <TeamMemberDetailsFormShadcn
              member={member}
              currentUser={currentUser}
              onUpdateMember={onUpdateMember}
              onUpdateRole={onUpdateRole}
              canEditMember={canEditMember}
              canChangeRole={canChangeRole}
              savingMember={savingMember}
              savingRole={savingRole}
              onChangesSaved={() => setHasUnsavedChanges(false)}
              onChangesDetected={() => setHasUnsavedChanges(true)}
            />
          </TabsContent>

          <TabsContent value="permissions" className="h-full m-0">
            <div className="p-6">
              <TeamMemberPermissions
                member={member}
                permissions={permissions}
                canEditPermissions={canChangeRole}
              />
            </div>
          </TabsContent>

          <TabsContent value="overview" className="h-full m-0">
            <div className="p-6">
              <TeamMemberOverview
                member={member}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Card>
  );
};

export default TeamMemberEditorShadcn;